/**
 * @fileOverview Error codes for AppError.
 *
 * Not all error codes will be defined here. Only the ones that are generally available and triggered by the app.
 * Some will be localised to their specific modules. For example Rockar Auth has its own error codes that are defined
 * inside the module.
 *
 * @module error-handling/error-codes
 */

/**
 * Error codes for AppError.
 */
export enum AppErrorCode {
	// Application codes
	APP_UNKNOWN_ERROR = 'APP_UNKNOWN_ERROR',
	// ⤷ This includes settings and translations fetch errors
	APP_CMS_DATA_FETCH_NOT_OK_ERROR = 'APP_CMS_DATA_FETCH_NOT_OK_ERROR',
	APP_CMS_DATA_FETCH_ERROR = 'APP_CMS_DATA_FETCH_ERROR',
	APP_CMS_DATA_VALIDATION_ERROR = 'APP_CMS_DATA_VALIDATION_ERROR',
	APP_CMS_TRANSLATIONS_FETCH_NOT_OK_ERROR = 'APP_CMS_TRANSLATIONS_FETCH_NOT_OK_ERROR',
	APP_CMS_TRANSLATIONS_FETCH_ERROR = 'APP_CMS_TRANSLATIONS_FETCH_ERROR',
	// ⤷ ENV variables errors
	APP_ENV_INVALID_ERROR = 'APP_ENV_INVALID_ERROR', // This is for when the environment variables are invalid
	APP_ENV_SERVER_ACCESS_ERROR = 'APP_ENV_SERVER_ACCESS_ERROR', // This is for when the client tries to access server environment variables
	// ⤷ Car configurator page errors
	APP_GET_ALL_CONFIGURATION_ITEMS_PARAMS_ERROR = 'APP_GET_ALL_CONFIGURATION_ITEMS_PARAMS_ERROR',
	APP_CAR_CONFIGURATION_NO_PART_TYPE_FOR_CAR_ERROR = 'APP_CAR_CONFIGURATION_NO_PART_TYPE_FOR_CAR_ERROR', // This is for when there is not a single available option for a part with the selected engine/grade combination
	// ⤷ SEAC page errors
	APP_CAR_NOT_FOUND_ERROR = 'APP_CAR_NOT_FOUND_ERROR', // This is for when the car is not found in the CarDB
	APP_COLOR_COMBINATION_NOT_FOUND_ERROR = 'APP_COLOR_COMBINATION_NOT_FOUND_ERROR', // This is for when the color combination is not found in the CarDB
	APP_MODEL_NOT_FOUND_ERROR = 'APP_MODEL_NOT_FOUND_ERROR', // This is for when the model is not found in the CarDB
	APP_WHEEL_OPTION_NOT_FOUND_ERROR = 'APP_WHEEL_OPTION_NOT_FOUND_ERROR', // This is for when the wheel option is not found in the CarDB

	APP_BASKET_UPDATE_ERROR = 'APP_BASKET_UPDATE_ERROR',
	APP_FINANCE_ERROR = 'APP_FINANCE_ERROR',
	APP_MONTHLY_PRICE_ERROR = 'APP_MONTHLY_PRICE_ERROR',
	APP_MONTHLY_PRICE_NO_LEGAL_DISCLAIMER_ERROR = 'APP_MONTHLY_PRICE_NO_LEGAL_DISCLAIMER_ERROR',
	APP_ORDER_CREATE_ERROR = 'APP_ORDER_CREATE_ERROR',
	APP_VEHICLE_ALLOCATION_ERROR = 'APP_VEHICLE_ALLOCATION_ERROR',

	// Mapbox codes
	MAPBOX_GEODISTANCE_ERROR = 'MAPBOX_GEODISTANCE_ERROR',
	MAPBOX_DRIVING_DISTANCES_ERROR = 'MAPBOX_DRIVING_DISTANCES_ERROR',

	// A2D codes
	DEALERS_A2D_ERROR = 'DEALERS_A2D_ERROR',
	DEALERS_A2D_NOT_FOUND = 'DEALERS_A2D_NOT_FOUND',
	AUTH_A2D_ERROR = 'AUTH_A2D_ERROR',

	// CarDB codes
	CARDB_GENERIC_ERROR = 'CARDB_GENERIC_ERROR',
	CARDB_FETCH_ERROR = 'CARDB_FETCH_ERROR',
	CARDB_MSLS_FETCH_ERROR = 'CARDB_MSLS_FETCH_ERROR',
	CARDB_MODEL_NOT_FOUND = 'CARDB_MODEL_NOT_FOUND',

	// Convert time codes
	CONVERT_TIME_INVALID_TIME_FORMAT = 'CONVERT_TIME_INVALID_TIME_FORMAT',

	// ALLOCATION ENGINE codes
	AE_ERROR = 'AE_ERROR',
	AE_ETA_ERROR = 'AE_ETA_ERROR',
	AE_STOCKLIST_ERROR = 'AE_STOCKLIST_ERROR',
	AE_TOKEN_ERROR = 'AE_TOKEN_ERROR',
	AE_DECRYPT_VIN_ERROR = 'AE_DECRYPT_VIN_ERROR',
	// ⤷ Allocation route specific codes
	AE_ALLOCATION_FAILED_SOME_VEHICLES_ERROR = 'AE_ALLOCATION_FAILED_SOME_VEHICLES_ERROR',
	AE_ALLOCATION_FAILED_ALL_VEHICLES_ERROR = 'AE_ALLOCATION_FAILED_ALL_VEHICLES_ERROR',
	AE_ALLOCATION_PARTIAL_SUCCESS = 'AE_ALLOCATION_PARTIAL_SUCCESS',
	AE_SEAC_ETA_CHANGED = 'AE_SEAC_ETA_CHANGED',
	AE_VIN_VEHICLE_UNAVAILABLE_ERROR = 'AE_VIN_VEHICLE_UNAVAILABLE_ERROR', // The specific unit requested by VIN is out of stock or already blocked by someone else
	// ⤷ Reallocation route specific codes
	AE_REALLOCATE_BASKET_UPDATE_ERROR = 'AE_REALLOCATE_BASKET_UPDATE_ERROR',
	AE_REALLOCATE_LOST_VEHICLES_ERROR = 'AE_REALLOCATE_LOST_VEHICLES_ERROR',
	AE_REALLOCATE_LOST_ALL_VEHICLES_ERROR = 'AE_REALLOCATE_LOST_ALL_VEHICLES_ERROR',
	// ⤷ Release route specific codes
	AE_RELEASE_BASKET_UPDATE_ERROR = 'AE_RELEASE_BASKET_UPDATE_ERROR',

	// TSF codes
	TFS_STANDARD_API_ERROR = 'TFS_STANDARD_API_ERROR',
	TFS_STANDARD_API_AUTH_ERROR = 'TFS_STANDARD_API_AUTH_ERROR',
	TFS_NO_FINANCE_PRODUCTS = 'TFS_NO_FINANCE_PRODUCTS',
	TFS_NO_DEFAULT_FINANCE_PRODUCTS = 'TFS_NO_DEFAULT_FINANCE_PRODUCTS',

	// TIS codes
	// ⤷ Makolab codes
	ML_MAKOLAB_INSURANCE_FETCH_ERROR = 'ML_MAKOLAB_INSURANCE_FETCH_ERROR',
	ML_MAKOLAB_INSURANCE_LEGAL_FETCH_ERROR = 'ML_MAKOLAB_INSURANCE_LEGAL_FETCH_ERROR',

	// IDR codes
	IDR_FETCH_ERROR = 'IDR_FETCH_ERROR',

	// Data
	DATA_MISSING = 'DATA_MISSING',
	DATA_MISSING_BLOCKED_VEHICLES = 'DATA_MISSING_BLOCKED_VEHICLES',

	// NextAuth
	NEXT_AUTH_ERROR = 'NEXT_AUTH_ERROR',

	// UNAUTHENTICATED
	CUSTOMER_NOT_AUTHENTICATED = 'CUSTOMER_NOT_AUTHENTICATED',
	COLLEAGUE_NOT_AUTHENTICATED = 'COLLEAGUE_NOT_AUTHENTICATED',

	//UNAUTHORIZED
	VIEW_ORDER_DETAILS_UNAUTHORIZED = 'VIEW_ORDER_DETAILS_UNAUTHORIZED',
	BASKET_INTERNAL_ORDER_UNAUTHORIZED = 'BASKET_INTERNAL_ORDER_UNAUTHORIZED',
	CHECKOUT_INTERNAL_ORDER_UNAUTHORIZED = 'CHECKOUT_INTERNAL_ORDER_UNAUTHORIZED',
	CONFIRMATION_INTERNAL_ORDER_UNAUTHORIZED = 'CONFIRMATION_INTERNAL_ORDER_UNAUTHORIZED',

	// Missing Params
	MISSING_PARAM_BASKET_ID = 'MISSING_PARAM_BASKET_ID',
	MISSING_PARAM_ORDER_ID = 'MISSING_PARAM_ORDER_ID',
	MISSING_PARAM_MODEL_YEAR = 'MISSING_PARAM_MODEL_YEAR',
	MISSING_PARAM_CONFIG_ORIGIN = 'MISSING_PARAM_CONFIG_ORIGIN',

	// Rockar
	ROCKAR_SAVE_CONFIG_ERROR = 'ROCKAR_SAVE_CONFIG_ERROR',
	ROCKAR_CREATE_BASKET_ERROR = 'ROCKAR_CREATE_BASKET_ERROR',
	ROCKAR_UPDATE_BASKET_ERROR = 'ROCKAR_UPDATE_BASKET_ERROR',
	ROCKAR_CREATE_ORDER_ERROR = 'ROCKAR_CREATE_ORDER_ERROR',
	ROCKAR_LOAD_ORDER_ERROR = 'ROCKAR_LOAD_CONFIRMATION_ERROR',
	ROCKAR_MISSING_ORDER_DATA_ERROR = 'ROCKAR_MISSING_ORDER_DATA_ERROR',
	ROCKAR_MUTATION_ERROR = 'ROCKAR_MUTATION_ERROR',
	ROCKAR_PRICE_ERROR = 'ROCKAR_PRICE_ERROR',
	ROCKAR_PRICE_NETWORK_ERROR = 'ROCKAR_PRICE_NETWORK_ERROR',
	ROCKAR_PRICE_GRAPHQL_ERROR = 'ROCKAR_PRICE_GRAPHQL_ERROR',
	ROCKAR_PRICE_EMPTY_RESPONSE_ERROR = 'ROCKAR_PRICE_EMPTY_RESPONSE_ERROR',
	ROCKAR_PROCESS_PAYMENT_ERROR = 'ROCKAR_PROCESS_PAYMENT_ERROR',
	// Rockar Vouchers
	ROCKAR_INVALID_VOUCHER = 'ROCKAR_INVALID_VOUCHER',

	//Features
	FEATURE_NOT_IMPLEMENTED_ERROR = 'FEATURE_NOT_IMPLEMENTED_ERROR',

	//Orders
	ORDER_CANCELLED = 'ORDER_CANCELLED',
	ORDER_FAILED = 'ORDER_FAILED',
	ADD_VOUCHER_CALLED_WITH_NO_BASKET_DATA = 'ADD_VOUCHER_CALLED_WITH_NO_BASKET_DATA',
	REMOVE_VOUCHER_CALLED_WITH_NO_BASKET_DATA = 'REMOVE_VOUCHER_CALLED_WITH_NO_BASKET_DATA',

	// PDF Page
	ORDER_GROUP_ID_NOT_FOUND = 'ORDER_GROUP_ID_NOT_FOUND',
	PDF_GENERATION_ERROR = 'PDF_GENERATION_ERROR',

	// Payment
	APP_FATAL_PAYMENT_ERROR = 'APP_FATAL_PAYMENT_ERROR',
	PAYMENT_SCRIPT_LOAD = 'PAYMENT_SCRIPT_LOAD',

	//CP (Customer Portal) errors
	CP_USER_BY_TOKEN_ERROR = 'CP_USER_BY_TOKEN_ERROR'
}

// By default we show the temporarily offline page on an error
// To show the generic error page (or SEAC error page when config origin is 'seac')
// instead add the error code here
export const ERROR_CODES_WHICH_TRIGGER_GENERIC_ERROR_PAGE = [
	AppErrorCode.APP_CAR_NOT_FOUND_ERROR,
	AppErrorCode.APP_COLOR_COMBINATION_NOT_FOUND_ERROR,
	AppErrorCode.APP_MODEL_NOT_FOUND_ERROR,
	AppErrorCode.APP_WHEEL_OPTION_NOT_FOUND_ERROR,
	AppErrorCode.DATA_MISSING_BLOCKED_VEHICLES,
	AppErrorCode.FEATURE_NOT_IMPLEMENTED_ERROR,
	AppErrorCode.ORDER_CANCELLED,
	AppErrorCode.ORDER_FAILED,
	AppErrorCode.ROCKAR_LOAD_ORDER_ERROR,
	AppErrorCode.VIEW_ORDER_DETAILS_UNAUTHORIZED,
	AppErrorCode.BASKET_INTERNAL_ORDER_UNAUTHORIZED,
	AppErrorCode.CHECKOUT_INTERNAL_ORDER_UNAUTHORIZED,
	AppErrorCode.CONFIRMATION_INTERNAL_ORDER_UNAUTHORIZED
];

export const ERROR_CODES_FATAL = [AppErrorCode.CARDB_FETCH_ERROR];
export const ERROR_CODES_CLIENT_LOGGING = [
	AppErrorCode.APP_FATAL_PAYMENT_ERROR,
	AppErrorCode.PAYMENT_SCRIPT_LOAD,
	AppErrorCode.DATA_MISSING,
	AppErrorCode.CUSTOMER_NOT_AUTHENTICATED
];
